import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/ui/ProductCard';
import { categoryIcons } from '../utils/helpers';
import './HomePage.css';

const categories = ['T-Shirt', 'Hoodie', 'Sweatshirt', 'Bag', 'Watch', 'Moneybag'];

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [homeReviews, setHomeReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/products/featured'),
      api.get('/reviews/home/featured'),
    ]).then(([prodRes, revRes]) => {
      setFeatured(prodRes.data);
      setHomeReviews(revRes.data);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb orb1" />
          <div className="hero-orb orb2" />
          <div className="hero-orb orb3" />
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <div className="hero-tag">New Collection 2026</div>
            <h1 className="hero-title">
              Wear Your <span className="gradient-text">Identity</span>
              <br />With <span className="hero-brand">NORIX</span>
            </h1>
            <p className="hero-desc">
              Premium clothing crafted for those who dare to express. Bold designs, timeless quality.
            </p>
            <div className="hero-btns">
              <Link to="/products" className="btn btn-primary hero-cta">
                Shop Now →
              </Link>
              <Link to="/products?category=T-Shirt" className="btn btn-outline">
                View Collection
              </Link>
            </div>
            <div className="hero-stats">
              <div className="hero-stat"><span>500+</span><p>Products</p></div>
              <div className="hero-stat-div" />
              <div className="hero-stat"><span>10K+</span><p>Customers</p></div>
              <div className="hero-stat-div" />
              <div className="hero-stat"><span>99%</span><p>Satisfaction</p></div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-img-wrap">
              {/* Floating badges */}
              <div className="hero-float-card card1">
                <span>🔥</span> Trending Now
              </div>
              <div className="hero-float-card card2">
                <span>⭐</span> 4.9 Rating
              </div>
              <div className="hero-float-card card3">
                <span>🚚</span> Fast Delivery
              </div>
              {/* Logo image inside glass circle */}
              <img
                src="https://res.cloudinary.com/gtoqqet4/image/upload/v1786193349/LOGO_mrgxxx.jpg"
                alt="NORIX"
                className="hero-logo-img"
              />
            </div>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="scroll-dot" />
        </div>
      </section>

      {/* Announcement Bar */}
      <div className="announce-bar">
        <div className="announce-track">
          {Array(4).fill(null).map((_, i) => (
            <span key={i}>
              🔥 New Arrivals &nbsp;•&nbsp;
              🚚 Dhaka Delivery ৳80 &nbsp;•&nbsp;
              💜 Pre-Order Available &nbsp;•&nbsp;
              ⭐ Premium Quality &nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* Categories */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Shop by <span className="gradient-text">Category</span></h2>
              <div className="divider" />
              <p className="section-sub">Find your perfect style from our curated collections</p>
            </div>
          </div>
          <div className="categories-grid">
            {categories.map((cat, i) => (
              <Link
                key={cat}
                to={`/products?category=${cat}`}
                className="category-card"
                style={{ '--delay': `${i * 0.05}s` }}
              >
                <div className="cat-icon">{categoryIcons[cat]}</div>
                <span className="cat-name">{cat}</span>
                <div className="cat-arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section featured-section">
        <div className="feat-bg" />
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Featured <span className="gradient-text">Products</span></h2>
              <div className="divider" />
              <p className="section-sub">Hand-picked pieces from our latest drops</p>
            </div>
            <Link to="/products" className="btn btn-outline">View All</Link>
          </div>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
              <div className="spinner" />
            </div>
          ) : featured.length > 0 ? (
            <div className="grid-4">
              {featured.map(product => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No featured products yet. Check back soon!</p>
              <Link to="/products" className="btn btn-primary">Browse All Products</Link>
            </div>
          )}
        </div>
      </section>

      {/* Why NORIX */}
      <section className="section why-section">
        <div className="container">
          <div className="section-header center">
            <h2 className="section-title">Why Choose <span className="gradient-text">NORIX</span>?</h2>
            <div className="divider" style={{ margin: '10px auto 20px' }} />
            <p className="section-sub">We don't just sell clothes, we craft experiences</p>
          </div>
          <div className="why-grid">
            {[
              { icon: '🧵', title: 'Premium Quality', desc: 'Crafted with the finest fabrics for lasting comfort and durability.' },
              { icon: '🎨', title: 'Unique Designs', desc: 'Exclusive patterns and colorways you won\'t find anywhere else.' },
              { icon: '🚚', title: 'Fast Delivery', desc: 'Quick delivery across Bangladesh. Dhaka ৳80, Outside ৳150.' },
              { icon: '🔄', title: 'Easy Returns', desc: 'Hassle-free return policy if you\'re not completely satisfied.' },
              { icon: '💳', title: 'Secure Payment', desc: 'Multiple payment options with advance and COD available.' },
              { icon: '🌟', title: 'Pre-Order', desc: 'Reserve exclusive items before they go live to the public.' },
            ].map((item, i) => (
              <div key={i} className="why-card card">
                <div className="why-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      {homeReviews.length > 0 && (
        <section className="section reviews-section">
          <div className="rev-bg" />
          <div className="container">
            <div className="section-header center">
              <h2 className="section-title">What Our <span className="gradient-text">Customers Say</span></h2>
              <div className="divider" style={{ margin: '10px auto 20px' }} />
              <p className="section-sub">Real reviews from real NORIX lovers</p>
            </div>
            <div className="reviews-grid">
              {homeReviews.map((rev, i) => (
                <div key={rev._id || i} className="review-card card">
                  <div className="rev-stars">{'★'.repeat(rev.rating || 5)}</div>
                  <p className="rev-comment">"{rev.comment}"</p>
                  <div className="rev-customer">
                    {rev.photo ? (
                      <img src={rev.photo} alt={rev.customerName} className="rev-avatar" />
                    ) : (
                      <div className="rev-avatar-placeholder">
                        {rev.customerName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="rev-name">{rev.customerName}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="cta-section">
        <div className="cta-bg" />
        <div className="container cta-content">
          <h2>Ready to Elevate Your Style?</h2>
          <p>Explore our full collection and find your perfect fit</p>
          <Link to="/products" className="btn btn-primary cta-btn">Shop the Collection →</Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
